import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { notificationService, type Notification } from '../services/notificationService';
import { MODAL_TOAST_MAX_WIDTH } from '@/styles/modalTokens';
import { Button } from '@/components/ui';
import { typeRole } from '@/styles/typography';

interface NotificationContainerProps {
    className?: string;
}

export default function NotificationContainer({ className = '' }: NotificationContainerProps) {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const location = useLocation();
    const isSupportPage = location.pathname.startsWith('/support');

    useEffect(() => {
        const unsubscribe = notificationService.subscribe(setNotifications);
        return unsubscribe;
    }, []);

    const getIcon = (type: string) => {
        // If on support page, use appropriate colors for icons
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return <CheckCircle className="w-5 h-5 text-green-400" />;
                case 'error':
                    return <AlertCircle className="w-5 h-5 text-red-400" />;
                case 'warning':
                    return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
                case 'info':
                    return <Info className="w-5 h-5 text-info-soft" />;
                default:
                    return <Info className="w-5 h-5 text-caption-muted" />;
            }
        }
        
        // Default icon colors for other pages
        switch (type) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-400" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
            case 'info':
                return <Info className="w-5 h-5 text-blue-400" />;
            default:
                return <Info className="w-5 h-5 text-caption-muted" />;
        }
    };

    const getBackgroundColor = (type: string) => {
        // If on support page, use solid background colors instead of transparent
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return 'bg-green-500/20 border-green-400/50 backdrop-blur-md';
                case 'error':
                    return 'bg-red-500/20 border-red-400/50 backdrop-blur-md';
                case 'warning':
                    return 'bg-yellow-500/20 border-yellow-400/50 backdrop-blur-md';
                case 'info':
                    return 'bg-info/20 border-info/50 backdrop-blur-md';
                default:
                    return 'bg-white/5 border-border-ice backdrop-blur-md';
            }
        }
        
        // Default transparent style for other pages
        switch (type) {
            case 'success':
                return 'bg-green-500/20 border-green-400/50 backdrop-blur-md';
            case 'error':
                return 'bg-red-500/20 border-red-400/50 backdrop-blur-md';
            case 'warning':
                return 'bg-yellow-500/20 border-yellow-400/50 backdrop-blur-md';
            case 'info':
                return 'bg-info/20 border-info/50 backdrop-blur-md';
            default:
                return 'bg-white/5 border-border-ice backdrop-blur-md';
        }
    };

    const getTextColor = (type: string) => {
        // If on support page, use dark text for contrast against light background
        if (isSupportPage) {
            switch (type) {
                case 'success':
                    return 'text-green-200';
                case 'error':
                    return 'text-red-200';
                case 'warning':
                    return 'text-yellow-200';
                case 'info':
                    return 'text-info-soft';
                default:
                    return 'text-ink';
            }
        }
        
        // Default text colors for other pages
        switch (type) {
            case 'success':
                return 'text-green-200';
            case 'error':
                return 'text-red-200';
            case 'warning':
                return 'text-yellow-200';
            case 'info':
                return 'text-info-soft';
            default:
                return 'text-ink';
        }
    };

    const handleDismiss = (id: string) => {
        notificationService.dismiss(id);
    };

    const handleAction = (action: () => void) => {
        action();
    };

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div 
            className={`fixed top-4 right-4 z-[var(--wf-z-modal-overlay)] space-y-3 w-full ${className}`}
            style={{ maxWidth: MODAL_TOAST_MAX_WIDTH }}
        >
            {notifications.map((notification) => (
                <div
                    key={notification.id}
                    className={`
                        transform transition-all duration-300 ease-in-out
                        ${notification.dismissed 
                            ? 'translate-x-full opacity-0 scale-95' 
                            : 'translate-x-0 opacity-100 scale-100'
                        }
                        ${getBackgroundColor(notification.type)}
                        border rounded-lg shadow-lg p-4
                        w-full
                    `}
                >
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            {getIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0 overflow-hidden">
                            {notification.title && (
                                <h4 className={`${typeRole.body} font-semibold ${getTextColor(notification.type)} mb-1 break-words`}>
                                    {notification.title}
                                </h4>
                            )}
                            
                            <p className={`${typeRole.body} ${getTextColor(notification.type)} break-words`}>
                                {notification.message && notification.message.trim() 
                                    ? notification.message.trim() 
                                    : (notification.type === 'error' ? 'An error occurred' : 'Notification')}
                            </p>

                            {notification.actions && notification.actions.length > 0 && (
                                <div className="mt-3 flex space-x-2">
                                    {notification.actions.map((action, index) => (
                                        <Button
                                            key={index}
                                            size="sm"
                                            variant={action.style === 'primary'
                                                ? 'moneyIn'
                                                : action.style === 'danger'
                                                ? 'destructive'
                                                : 'tertiary'
                                            }
                                            onClick={() => handleAction(action.action)}
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => handleDismiss(notification.id)}
                            aria-label="Dismiss notification"
                            className={`
                                flex-shrink-0 p-1 rounded-full transition-colors
                                ${isSupportPage 
                                    ? 'text-caption-muted hover:bg-white/10' 
                                    : `${getTextColor(notification.type)} hover:bg-white/10`
                                }
                            `}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
